import { DateFormat } from '@equip.health/ui';
import { isoToTimezoneDateTime, parseDateOnlyInTimezone } from '../../utils';
import {
  formatRangeLabel,
  formatSlotTime,
  timeOfDayLabel,
  weekMonday,
  weeksBetweenMondays,
} from './datetime.util';


export const getCalendarWeekCount = (
  anchorISO: string,
  windows: FollowUpWindow[],
  timezone?: string,
): number => {
  if (!windows.length) return 0;
  const intakeMonday = weekMonday(isoToTimezoneDateTime(anchorISO, timezone));
  const lastEndISO = windows.at(-1)!.endISO;
  const lastCovered = isoToTimezoneDateTime(lastEndISO, timezone).minus({
    milliseconds: 1,
  });
  const lastMonday = weekMonday(lastCovered);
  return Math.max(0, weeksBetweenMondays(intakeMonday, lastMonday) + 1);
};

const buildOpening = (
  slot: AvailabilityAnchorSlot,
  timezone?: string,
): FollowUpOpening => {
  const start = isoToTimezoneDateTime(slot.start, timezone);
  return {
    startTime: slot.start,
    endTime: slot.end,
    providerId: slot.users![0].id,
    seriesKey: `${start.weekday}|${start.toFormat('HH:mm')}`,
    weekdayLabel: start.toFormat('ccc'),
    dateLabel: start.toFormat(DateFormat.shortDayMonthDateNoPad),
    timeLabel: timeOfDayLabel(start),
    formattedStartEndTime: formatSlotTime(slot.start, slot.end, timezone),
  };
};

export const buildFollowUpWeekBuckets = (
  openings: AvailabilityAnchorSlot[],
  anchorISO: string,
  weekCount: number,
  timezone?: string,
  anchorEndISO?: string,
  admitISO?: string,
): FollowUpWeekBucket[] => {
  if (weekCount <= 0) return [];

  const anchorInstant = isoToTimezoneDateTime(anchorISO, timezone);
  const anchorEnd = isoToTimezoneDateTime(anchorEndISO ?? anchorISO, timezone);
  const intakeMonday = weekMonday(anchorInstant);

  const labelBaseOffset = admitISO
    ? Math.max(
        0,
        weeksBetweenMondays(
          weekMonday(parseDateOnlyInTimezone(admitISO, timezone)),
          intakeMonday,
        ),
      )
    : 0;

  const buckets: FollowUpWeekBucket[] = [];
  for (let i = 0; i < weekCount; i += 1) {
    const start = intakeMonday.plus({ weeks: i });
    const end = start.plus({ days: 6 });
    buckets.push({
      weekIndex: i,
      startDate: start.toISODate()!,
      endDate: end.toISODate()!,
      weekLabel: `Week ${labelBaseOffset + i + 1}`,
      startLabel: start.toFormat(DateFormat.shortMonthDayNoPad),
      rangeLabel: formatRangeLabel(start, end),
      openings: [],
    });
  }

  for (const slot of openings) {
    if (!slot.users?.length) continue;
    const start = isoToTimezoneDateTime(slot.start, timezone);
    const index = weeksBetweenMondays(intakeMonday, weekMonday(start));
    if (index < 0 || index >= weekCount) continue;
    if (start < anchorEnd || start <= anchorInstant) continue;
    buckets[index].openings.push(buildOpening(slot, timezone));
  }

  for (const bucket of buckets) {
    bucket.openings.sort(
      (a, b) =>
        new Date(a.startTime).getTime() - new Date(b.startTime).getTime(),
    );
  }

  return buckets;
};

export const getQualifyingWeekCount = (
  buckets: FollowUpWeekBucket[],
): number =>
  buckets.filter((b) => b.weekIndex > 0 && b.openings.length > 0).length;

export const getBookableWeekCount = (
  buckets: FollowUpWeekBucket[],
): number => buckets.filter((b) => b.openings.length > 0).length;
